import {transparentize} from 'polished';
import React, {FC, useRef, useState} from 'react';
import styled from 'styled-components';

import {KeyboardActionsEnum} from '../../../../../../libs/shared/core/src/helpers/browser/keyboardHelpers';
import {useInitialLayoutEffect} from '../../../../../../libs/shared/core/src/helpers/react/hooks/useEffectHelpers';
import {KeyboardEventHandler} from '../../../../../../libs/shared/core/src/helpers/react/reactHelpers';
import {leaveBreadcrumb} from '../../../../../../libs/shared/core/src/services/bugsnag/bugsnag';
import {BoxedInput} from '../../../../../../libs/shared/ui/src/components/boxedInputs/boxedInput';
import {KeyboardShortcuts} from '../../../../../../libs/shared/ui/src/components/keyboard/keyboardShortcuts';
import {center} from '../../../../../../libs/shared/ui/src/styles/mixins';
import {ConversationSearchContextProps} from '../highlights/conversationSearchContext';
import {ConversationHeaderSearchBar} from './conversationHeaderSearchBar';

/*
 * Props.
 */

interface ConversationHeaderSearchProps {
  conversationSearchContext: ConversationSearchContextProps;
}

/*
 * Style.
 */

const StyledSearchDiv = styled.div`
  position: absolute;
  right: 16px;
  max-width: 100%;
  top: -14px;
  bottom: -14px;
  padding-left: 2px;
  height: 36px;
  margin: 12px auto;

  background-color: ${(p) => p.theme.greys.shade10};
  ${center()};

  &::before {
    content: '';
    position: absolute;
    width: 73px;
    height: 100%;
    left: -73px;
    background-image: linear-gradient(
      90deg,
      ${(p) => transparentize(1, p.theme.greys.shade10)},
      ${(p) => p.theme.greys.shade10}
    );
  }
`;

/*
 * Component.
 */

export const ConversationHeaderSearch: FC<ConversationHeaderSearchProps> = (props) => {
  const searchInputRef = useRef<BoxedInput>(null);
  const [isSearchOpen, setIsSearchOpen] = useState(false);

  const {conversationSearchContext} = props;
  const {resetQuery} = conversationSearchContext;

  // Reset query when this component is unmounted.
  // This will remove all highlights.
  useInitialLayoutEffect(() => resetQuery);

  /*
   * Event handlers.
   */

  const onSearchShortcut: KeyboardEventHandler = (event) => {
    event.preventDefault();
    leaveBreadcrumb('Opened in-conversation search', {});

    // If the search is already open, focus it.
    const currentSearchInput = searchInputRef.current;
    if (currentSearchInput && isSearchOpen) {
      currentSearchInput.focus(true);
      return;
    }

    // Otherwise, open it.
    setIsSearchOpen(true);
  };

  const onRequestClose = () => {
    leaveBreadcrumb('Closed in-conversation search', {});
    resetQuery();
    setIsSearchOpen(false);
  };

  const keyHandlers = {
    [KeyboardActionsEnum.SEARCH_IN_CONVERSATION]: onSearchShortcut,
    [KeyboardActionsEnum.CLOSE]: isSearchOpen ? onRequestClose : undefined,
  };

  const renderSearch = () => {
    if (!isSearchOpen) {
      return null;
    }

    return (
      <StyledSearchDiv>
        <ConversationHeaderSearchBar
          onCloseClick={onRequestClose}
          inputRef={searchInputRef}
          conversationSearchContext={conversationSearchContext}
        />
      </StyledSearchDiv>
    );
  };

  return <KeyboardShortcuts handlers={keyHandlers}>{renderSearch()}</KeyboardShortcuts>;
};
